import React, { useEffect, useState } from "react";
import { Table, Button, Select, Input, Row, Col, Tag, notification, Modal, Typography } from "antd";
import { EnvironmentOutlined, ExportOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import { MesServices } from "../Services/MesServices";

import * as XLSX from "xlsx";
const { Title, Text } = Typography;
const { Option } = Select;
const FournisseursSoins = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // Pour stocker les données filtrées
  const [specialitesData, setSpecialitesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedSpecialite, setSelectedSpecialite] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const mesServices = new MesServices();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const benefData = await mesServices.getFournisseurs();
        setData(benefData);
        setFilteredData(benefData); // Initialiser les données filtrées
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSpecialite = async () => {
      setLoading(true);
      try {
        const specialiteData = await mesServices.getSpecialites();
        setSpecialitesData(specialiteData);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchSpecialite();
  }, []);

  // Fonction pour filtrer les données en fonction de la spécialité sélectionnée
  const handleSpecialiteChange = (value) => {
    setSelectedSpecialite(value || "");
  };

  const handleOpenModal = (record) => {
    setSelectedRow(record); // Stocker les données de la ligne
    setIsModalVisible(true); // Afficher le modal
  };

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRow(null); // Réinitialiser la ligne sélectionnée
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Code",
      dataIndex: "numFourn",
      key: "numFourn",
      align: "center",
      sorter: true,
    },
    {
      title: "Raison Sociale",
      dataIndex: "raisoc",
      key: "raisoc",
      sorter: true,
    },
    {
      title: "Adresse",
      dataIndex: "adresse",
      key: "adresse",
    },

    {
      title: "Agrément",
      dataIndex: "agree",
      key: "agree",
      align: "center",
      filters: [
        { text: "Oui", value: "A" },
        { text: "Non", value: "N" },
      ],
      onFilter: (value, record) => record.agree === value,
      render: (value) =>
        value === "A" ? (
          <Tag color="success">Oui</Tag>
        ) : (
          <Tag color="error">Non</Tag>
        ),
    },

    {
      title: "Tél. Fixe",
      dataIndex: "telFix",
      key: "telFix",
    },
    {
      title: "Tél. Portable",
      dataIndex: "portable",
      key: "portable",
    },
    {
      title: "Spécialité",
      dataIndex: "intitule",
      key: "intitule",
    },
    {
      title: "",
      key: "actions",
      align: "center",
      render: (text, record) => (
        <Button
          type="primary"
          shape="circle"
          icon={<EnvironmentOutlined />}
          onClick={() => handleOpenModal(record)} // Ouvre le modal
        />
      ),
    },
  ];

  const handleExport = () => {
    try {
      // Préparer les données à exporter
      const exportData = filteredData.map((item) => ({
        Code: item.numFourn,
        Raison_Social: item.raisoc,
        Adresse: item.adresse,
        Tel_Fixe: item.telFix,
        Tel_Portable: item.portable,
        Specialité: item.intitule

      }));

      // Créer une feuille de calcul à partir des données
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Créer un classeur
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");

      // Générer et télécharger le fichier Excel
      XLSX.writeFile(workbook, "listes_des_fournisseurs_de_soins.xlsx");
      notification.success({ message: "Fichier exporté avec succès !" });
    } catch (error) {
      console.error("Erreur lors de l'exportation :", error);
      notification.error({
        message: "Erreur",
        description: "Une erreur s'est produite lors de l'exportation.",
      });
    }
  };
  // Fonction pour appliquer les filtres combinés
  const applyFilters = () => {
    let filtered = data.filter((item) => item.agree === "A");

    // Filtrage par spécialité
    if (selectedSpecialite) {
      filtered = filtered.filter((item) => item.codespe === selectedSpecialite);
    }

    // Filtrage par recherche textuelle
    if (searchText) {
      filtered = filtered.filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };



  // Gestion de la recherche
  const handleSearchChange = (e) => {
    setSearchText(e.target.value); // Met à jour le texte de recherche
  };

  // Réapplique les filtres à chaque changement
  useEffect(() => {
    applyFilters();
  }, [searchText, selectedSpecialite, data]);

  const mapQuery = [
    selectedRow?.adresse,
    selectedRow?.raisoc,
    selectedRow?.regin,
    selectedRow?.pays,
  ]
    .filter((value) => typeof value === "string" && value.trim())
    .join(", ");

  const encodedMapQuery = encodeURIComponent(mapQuery);

  return (
    <div style={{
      padding: "20px",
      background: "linear-gradient(115deg, #eef2fc 0%, #e4f5ec 38%, #f9eedd 100%)",
      minHeight: "100vh",
      transition: "background 0.7s ease-in-out"
    }}>
      <h2 style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: 10 }}>
        <MedicineBoxOutlined style={{ color: "#1f4ea1", fontSize: 28 }} />
        Liste des Fournisseurs de Soins
      </h2>

      <Row justify="space-between" style={{ marginBottom: "10px" }}>
        <Col>
          <Select
            defaultValue="Choisir"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: 250, marginRight: "10px" }}
            onChange={handleSpecialiteChange} // Gérer le changement de sélection
          >
            <Option value="">Toutes les spécialités</Option>
            {specialitesData &&
              specialitesData.map((specialite) => (
                <Option key={specialite.codespe} value={specialite.codespe}>
                  {`${specialite.codespe} - ${specialite.intitule}`}
                </Option>
              ))}
          </Select>
          <Input
            placeholder="Rechercher..."
            allowClear
            style={{ width: 200 }}
            onChange={handleSearchChange}
          />
        </Col>
        <Col>
          <Button /* type="primary" */ className="buttonExport" onClick={handleExport} icon={<ExportOutlined />}>
            Export
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        loading={loading}
        dataSource={filteredData}
        scroll={{ x: true }}
        rowKey="numFourn" // Assurez-vous que chaque ligne a une clé unique
        bordered
        // size="small"
        style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
        className="custom-header-table my-custom-table"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total : ${total}`,
        }}
      />

      <Modal
        width={800}
        title={selectedRow ? `${selectedRow.raisoc} (${selectedRow.intitule})` : "Détails"}
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null} // Pas de boutons par défaut
      >
        {selectedRow && (
          <div style={{ lineHeight: "2" }}>
            {/*  <Title level={5}>{`DR. ${selectedRow.raisoc}`}</Title> */}
            <Text strong>Adresse :</Text><strong> {selectedRow.adresse}</strong>
            <br />
            <Text strong>Tél Fixe :</Text> <strong>{selectedRow.telFix}</strong>
            <br />
            <Text strong>Tél Mobile :</Text> <strong>{selectedRow.portable}</strong>
            <br />{/* 

            <Text strong>Spécialité :</Text> <strong>{selectedRow.intitule}</strong> */}
            {/* <Text strong>Géolocalisation :</Text> */}
            {mapQuery ? (
              <div style={{ marginTop: "10px" }}>
                <iframe
                  title="map"
                  width="100%"
                  height="200"
                  frameBorder="0"
                  style={{ border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodedMapQuery}&z=15&output=embed`}
                  allowFullScreen
                />

              </div>
            ) : (
              <Text type="secondary">Adresse non disponible</Text>
            )}


          </div>
        )}
      </Modal>
    </div>
  );
};

export default FournisseursSoins;
